import { ButtonHTMLAttributes, forwardRef } from "react";

import { LtIconsId } from "@landtechnologies/icons/font/lt-icons";
import clsx from "clsx";

export const IconAnimations = ["spin"] as const;
type IconAnimation = (typeof IconAnimations)[number];
export const ButtonVariants = [
  "primary",
  "secondary",
  "tertiary",
  "ghost",
  "clear",
  "warning",
  "danger",
  "brand-primary",
  "brand-accent-primary",
  "brand-accent-secondary",
  "brand-secondary",
  "ai",
  "link",
] as const;
type ButtonVariant = (typeof ButtonVariants)[number];

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  disabled?: boolean;
  error?: boolean;
  leadIcon?: LtIconsId;
  leadIconAnimation?: IconAnimation;
  tailIcon?: LtIconsId;
  tailIconAnimation?: IconAnimation;
  iconSize?: "3xl" | "2xl" | "xl" | "lg" | "md" | "sm";
  variant?: ButtonVariant;
  size?: "lg" | "md" | "sm" | "xs";
  clickBufferSize?: "sm" | "md" | "lg";
};

const primaryStyles = clsx(
  "text-text-base-inverted bg-background-button-primary ring-border-button-focus",
  "group-enabled:hover:bg-background-button-primary-hover",
  "group-enabled:active:bg-background-button-primary-pressed [&.active]:bg-background-button-primary-pressed",
  "group-enabled:focus:ring-2",
  "group-disabled:bg-background-button-primary-disabled group-disabled:text-text-base-primary-disabled",
  "group-data-[error=true]:bg-negative-600 group-data-[error=true]:hover:bg-negative-700 group-data-[error=true]:focus:bg-negative-800 group-data-[error=true]:ring-border-action-destructive"
);

const brandPrimaryStyles = clsx(
  "text-text-base-inverted bg-background-button-brand-primary ring-border-button-brand",
  "group-enabled:hover:bg-background-button-brand-primary-hover",
  "group-enabled:active:bg-background-button-brand-primary-pressed [&.active]:bg-background-button-brand-primary-pressed",
  "group-enabled:focus:ring-2",
  "group-disabled:bg-background-button-brand-primary-disabled group-disabled:text-text-base-primary-disabled"
);

const brandAccentPrimaryStyles = clsx(
  "text-text-base bg-background-button-brand-accent-primary ring-border-button-brand-accent",
  "group-enabled:hover:bg-background-button-brand-accent-primary-hover group-enabled:hover:text-text-base-inverted",
  "group-enabled:active:bg-background-button-brand-accent-primary-pressed [&.active]:bg-background-button-brand-accent-primary-pressed",
  "group-enabled:focus:ring-2",
  "group-disabled:bg-background-button-brand-accent-primary-disabled group-disabled:text-text-base-primary-disabled"
);

const secondaryStyles = clsx(
  "text-text-base-primary bg-background-button-secondary ring-border-button-secondary ring-2",
  "group-enabled:hover:bg-background-button-secondary-hover group-hover:ring-border-button-secondary-hover",
  "group-enabled:active:bg-background-button-secondary-pressed group-active:ring-border-button-secondary-pressed [&.active]:bg-background-button-secondary-pressed [&.active]:ring-border-button-secondary-pressed",
  "group-enabled:focus:ring-border-button-focus",
  "group-disabled:bg-background-button-secondary-disabled group-disabled:ring-border-button-disabled group-disabled:text-text-base-secondary-disabled"
);

const brandSecondaryStyles = clsx(
  "text-text-base-primary bg-background-button-brand-secondary ring-border-button-brand ring-2",
  "group-enabled:hover:bg-background-button-brand-secondary-hover group-hover:ring-border-button-brand-hover",
  "group-enabled:active:bg-background-button-brand-secondary-pressed group-active:ring-border-button-brand-pressed [&.active]:bg-background-button-brand-secondary-pressed [&.active]:ring-border-button-brand-pressed",
  "group-enabled:focus:ring-border-button-brand",
  "group-disabled:bg-background-button-brand-secondary-disabled group-disabled:ring-border-button-disabled group-disabled:text-text-base-primary-disabled"
);

const brandAccentSecondaryStyles = clsx(
  "text-text-base-primary bg-background-button-brand-accent-secondary ring-border-button-brand-accent ring-2",
  "group-enabled:hover:bg-background-button-brand-secondary-hover group-hover:ring-border-button-brand-accent-hover",
  "group-enabled:active:bg-background-button-brand-accent-secondary-pressed group-active:ring-border-button-brand-accent-pressed [&.active]:bg-background-button-brand-accent-secondary-pressed [&.active]:ring-border-button-brand-pressed",
  "group-enabled:focus:ring-border-button-brand-accent",
  "group-disabled:bg-background-button-brand-accent-secondary-disabled group-disabled:ring-border-button-disabled group-disabled:text-text-base-primary-disabled"
);

const tertiaryStyles = clsx(
  "text-text-base-primary bg-background-button-tertiary ring-border-button-focus",
  "group-enabled:hover:bg-background-button-tertiary-hover",
  "group-enabled:active:bg-background-button-tertiary-pressed [&.active]:bg-background-button-tertiary-pressed",
  "group-enabled:focus:ring-border-button-focus",
  "group-disabled:bg-background-button-tertiary-disabled group-disabled:text-text-base-primary-disabled"
);

const ghostStyles = clsx(
  "bg-transparent ring-border-button-focus/20",
  "group-enabled:hover:bg-background-button-tertiary",
  "group-enabled:active:bg-background-button-tertiary-hover [&.active]:bg-background-button-tertiary-hover",
  "group-enabled:focus:ring-2",
  "group-disabled:text-text-base-primary-disabled"
);

const clearStyles = clsx(
  "bg-transparent text-text-base-primary",
  "group-enabled:hover:bg-background-button-tertiary",
  "group-enabled:active:bg-background-button-tertiary-hover [&.active]:bg-background-button-tertiary-hover",
  "group-enabled:focus:ring-2",
  "group-disabled:text-text-base-primary-disabled"
);

const warningStyles = clsx(
  "text-text-base-inverted bg-warning-600 ring-border-button-focus",
  "group-enabled:hover:bg-warning-700",
  "group-enabled:active:bg-warning-800 [&.active]:bg-warning-800",
  "group-enabled:focus:ring-2",
  "group-disabled:bg-warning-300 group-disabled:text-text-base-primary-disabled"
);
const dangerStyles = clsx(
  "text-text-base-inverted bg-negative-600 ring-border-button-focus",
  "group-enabled:hover:bg-negative-700",
  "group-enabled:active:bg-negative-800 [&.active]:bg-negative-800",
  "group-enabled:focus:ring-2",
  "group-disabled:bg-negative-300 group-disabled:text-text-base-primary-disabled"
);

const aiStyles = clsx(
  "text-text-base-inverted bg-linear-to-r from-platform-500 to-brand-500  ring-border-button-focus",
  "group-enabled:hover:from-platform-600 group-enabled:hover:to-brand-600",
  "group-enabled:active:from-platform-700 [&.active]:from-platform-700 group-enabled:active:to-brand-700 [&.active]:to-brand-700",
  "group-enabled:focus:ring-2",
  "group-disabled:from-platform-200 group-disabled:to-brand-200 group-disabled:text-text-base-primary-disabled"
);

const linkStyles = clsx(
  "bg-transparent text-platform-blue-500 underline",
  "group-enabled:hover:text-platform-blue-700",
  "group-enabled:active:bg-background-button-tertiary-hover [&.active]:bg-background-button-tertiary-hover",
  "group-enabled:focus:ring-2",
  "group-disabled:text-text-base-primary-disabled"
);

const variants: Record<ButtonVariant, string> = {
  primary: primaryStyles,
  "brand-primary": brandPrimaryStyles,
  "brand-secondary": brandSecondaryStyles,
  "brand-accent-primary": brandAccentPrimaryStyles,
  "brand-accent-secondary": brandAccentSecondaryStyles,
  secondary: secondaryStyles,
  tertiary: tertiaryStyles,
  ghost: ghostStyles,
  clear: clearStyles,
  warning: warningStyles,
  danger: dangerStyles,
  ai: aiStyles,
  link: linkStyles,
};

/**
 * The default button used in the app. Supports:
 *
 * - a number of variants (primary, secondary, tertiary, ghost, clear, danger)
 * - a number of states (disabled, error)
 * - lead and trailing icons that can be animated independently
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "sm",
      className,
      error,
      leadIcon,
      tailIcon,
      iconSize = "lg",
      leadIconAnimation,
      tailIconAnimation,
      clickBufferSize,
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        data-error={error}
        className={clsx(
          "group",
          clickBufferSize === "sm" && "-m-2 p-2",
          clickBufferSize === "md" && "-m-3 p-3",
          clickBufferSize === "lg" && "-m-5 p-5"
        )}
        {...rest}
      >
        <div
          className={clsx(
            "inline-flex items-center justify-center relative isolate gap-x-2 px-2 py-1.5 rounded-lg ring-inset",
            variants[variant],
            {
              "cursor-pointer": !rest.disabled,
              "shadow-xs": variant !== "ghost" && variant !== "link",
              "text-sm": size === "sm" || size === "md",
              "text-xs": size === "xs",
              "text-base": size === "lg",
              "py-2 px-3": size === "md",
              "py-3 px-4": size === "lg",
            },
            className
          )}
        >
          {leadIcon && <ButtonIcon icon={leadIcon} animation={leadIconAnimation} size={iconSize} />}
          {children}
          {tailIcon && <ButtonIcon icon={tailIcon} animation={tailIconAnimation} size={iconSize} />}
        </div>
      </button>
    );
  }
);

interface ButtonIconProps {
  icon: LtIconsId;
  animation?: IconAnimation;
  size?: "3xl" | "2xl" | "xl" | "lg" | "md" | "sm";
}

function ButtonIcon({ icon, size = "lg", animation }: ButtonIconProps) {
  return (
    <i
      className={clsx(`text-${size} leading-5 icon-${icon}`, {
        "animate-spin": animation === "spin",
      })}
    />
  );
}

Button.displayName = "Button";
